import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { useAuthStore } from '../../stores/auth.store';
import { Icon } from '../../components/ui/Icon';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { Chip } from '../../components/ui/Chip';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { SkeletonKPIRow, SkeletonTable } from '../../components/ui/Skeleton';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { fmtEur } from '../../lib/utils';
import { adminLinks } from '../../lib/adminRoutes';
import './suppliers.css';

interface Supplier {
  id: string; fullName: string; vat: string | null; email: string | null; city: string | null;
  hourlyRate: string | null; active: boolean; skills: { name: string; level: number }[];
  /** Persona fisica (professionista) invece che società: lo dice l'anagrafica FattureInCloud. */
  isPerson: boolean;
  /** Valorizzato quando lo stesso soggetto è anche un cliente: il rimando alla sua scheda CRM. */
  alsoClient: { id: string; shortName: string } | null;
  payablesCount: number;
  payablesTotal: number;
}

interface PaginatedSuppliers {
  items: Supplier[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function SuppliersPage() {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const user = useAuthStore((s) => s.user);
  const showMargin = !!user?.role.canSeeMargin;
  const [search, setSearch] = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 220);
    return () => clearTimeout(t);
  }, [search]);

  // Cambiando la ricerca si torna a pagina 1 (altrimenti rischi una pagina vuota)
  useEffect(() => { setPage(1); }, [debSearch]);

  const params = new URLSearchParams();
  if (debSearch) params.set('search', debSearch);
  params.set('page', String(page));
  params.set('pageSize', String(PAGE_SIZE));
  const qs = params.toString();

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', debSearch, page],
    queryFn: () => api.get<PaginatedSuppliers>(`/suppliers?${qs}`),
  });

  // Query separata senza filtro search: serve per le KPI in alto (attivi, skill, tariffa media, totale)
  // che devono restare stabili indipendentemente dalla ricerca corrente.
  const { data: allData } = useQuery({
    queryKey: ['suppliers', 'stats'],
    queryFn: () => api.get<PaginatedSuppliers>('/suppliers?pageSize=200'),
  });

  return (
    <div className="page">
      {/* ── RIFACIMENTO EDITORIALE: titolo grande e leggero, hairline aurora,
            numeri NUDI sul foglio (stat-band), zero scatole ── */}
      <div className="page-ed-hd flex items-end justify-between gap-4">
        <div>
          <h1 className="ed-title">Professionisti</h1>
          <div className="ed-sub">
            Le persone che lavorano con noi e ci fatturano. Le società fornitrici stanno in{' '}
            {/* sup-hit allarga il bersaglio a 44px sul telefono senza gonfiare
                il testo: è un link dentro una frase, ingrandirlo la spezzerebbe. */}
            <a className="underline cursor-pointer sup-hit" onClick={() => navigate(adminLinks.fornitori())}>Amministrazione, Spese</a>.
          </div>
        </div>
        {/* d-only: sul telefono l'azione principale non sta in cima, dove il
            pollice non arriva, ma nella barra in fondo (mobile.md §4). */}
        <div className="flex gap-2 pb-1 d-only">
          <button className="btn btn-primary" onClick={() => openModal('newVendor')}><Icon name="plus" /> Nuovo professionista</button>
          <ActionsMenu
            variant="ghost"
            items={[
              { key: 'export', label: 'Esporta', icon: 'export', onClick: () => openModal('export', { what: 'Professionisti' }) },
            ]}
          />
        </div>
      </div>

      {isLoading && !data ? <SkeletonKPIRow count={4} /> : (
      <div className="stat-band">
        <div>
          <div className="st-label">Professionisti attivi</div>
          <div className="st-value"><AnimatedNumber value={allData?.items.filter((s) => s.active).length ?? 0} /></div>
          <div className="st-delta">di cui {(allData?.items ?? []).filter((s) => s.isPerson).length} persone fisiche</div>
        </div>
        <div>
          {/* Quanto ci costano davvero: è il numero che dice se l'anagrafica
              serve a qualcosa, molto più delle skill registrate a mano. */}
          <div className="st-label">Speso con loro</div>
          <div className="st-value"><span className="u">€ </span><AnimatedNumber value={(allData?.items ?? []).reduce((a, s) => a + s.payablesTotal, 0)} format={fmtEur} /></div>
          <div className="st-delta">{(allData?.items ?? []).reduce((a, s) => a + s.payablesCount, 0)} fatture ricevute</div>
        </div>
        {showMargin
          ? (() => {
              const rates = (allData?.items ?? []).map((s) => Number(s.hourlyRate ?? 0)).filter((r) => r > 0);
              const avg = rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length) : 0;
              return (
                <div>
                  <div className="st-label">Tariffa media</div>
                  {rates.length
                    ? <div className="st-value"><span className="u">€</span><AnimatedNumber value={avg} /></div>
                    : <div className="st-value mute-2">—</div>}
                  {/* Su quante è calcolata: la media di 3 tariffe su 105 schede
                      non è "la tariffa media dei fornitori", e va detto. */}
                  <div className="st-delta">{rates.length ? `all'ora · su ${rates.length} con tariffa` : 'nessuna tariffa impostata'}</div>
                </div>
              );
            })()
          : (
            <div>
              <div className="st-label">Tariffa media</div>
              <div className="st-value mute-2">•••</div>
              <div className="st-delta">Riservato</div>
            </div>
          )}
        <div>
          {/* Chi compare da tutte e due le parti: il numero che dice quanto
              conta la vista unificata cliente/fornitore che arriverà. */}
          <div className="st-label">Anche clienti</div>
          <div className="st-value"><AnimatedNumber value={(allData?.items ?? []).filter((s) => s.alsoClient).length} /></div>
          <div className="st-delta">su {allData?.items.length ?? 0} anagrafiche</div>
        </div>
      </div>
      )}

      {/* mb-4 + flex-wrap: è la forma canonica della barra filtri (19 schermate
          su 21). Senza mb-4 il margine resta 0 e, collassando con
          .ed-section { margin-top: 4 }, la barra finiva a 4px dalla tabella
          invece dei 16 di tutte le altre. */}
      <MobileFilters attivi={search.trim() ? 1 : 0}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search" style={{ width: 280, margin: 0 }}>
          <Icon name="search" size={12} />
          <input placeholder="Cerca per nome, email, città…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="spacer" />
        <span className="mono mute text-[12px]">{data?.total ?? 0} risultati</span>
      </div>
      </MobileFilters>

      {/* Full-bleed: la lista vive sul foglio, senza gabbia */}
      <div className="ed-section">
        {/* tbl--card: sotto i 768px ogni riga diventa una scheda. Sul computer
            non cambia niente — la classe non ha regole fuori dalla media query. */}
        <table className="tbl tbl--ed tbl--card">
          {/* Niente colonne-codice in lista: P.IVA resta nel dettaglio; l'email scende
              come riga secondaria del nome (cell-2l) invece di occupare una colonna. */}
          <thead>
            <tr>
              <th style={{ width: 30 }} /><th>Nome</th><th>Città</th>
              <th className="num">Fatture</th>
              <th className="num">Speso</th>
              {showMargin && <th className="num">Tariffa</th>}
              <th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !data && <SkeletonTable rows={5} cols={showMargin ? 7 : 6} />}
            {(data?.items ?? []).map((s) => (
              <tr key={s.id} onClick={() => navigate(`/suppliers/${s.id}`)}>
                {/* Le iniziali senza il nome accanto non dicono niente: sul
                    telefono spariscono, il nome è già il titolo della scheda. */}
                <td data-m="off"><Avatar initials={s.fullName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()} /></td>
                <td data-m="hd" className="cell-2l">
                  <div className="p flex items-center gap-2">
                    <span className="truncate">{s.fullName}</span>
                    {/* Soggetto che è anche cliente: si segna, non si fonde.
                        Il click porta alla sua scheda CRM senza passare da qui. */}
                    {s.alsoClient && (
                      <span
                        className="chip-label"
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${s.alsoClient!.id}`); }}
                        title={`Anche cliente · ${s.alsoClient.shortName}`}
                      >
                        anche cliente
                      </span>
                    )}
                  </div>
                  {/* Riga secondaria: la mail se c'è, altrimenti la P.IVA —
                      su 105 anagrafiche arrivate da FIC la mail manca quasi
                      sempre, e una riga vuota non direbbe niente. */}
                  <div className="s">{s.email ?? s.vat ?? (s.isPerson ? 'professionista' : '—')}</div>
                </td>
                <td data-m="key" data-l="Città">{s.city ?? '—'}</td>
                <td data-m="key" data-l="Fatture" className="num mono mute">{s.payablesCount || '—'}</td>
                <td data-m="key" data-l="Speso" className="num tbl-metric">{s.payablesTotal ? `€ ${fmtEur(s.payablesTotal)}` : '—'}</td>
                {/* Tariffa solo dove esiste: "€ 0/h" su 100 righe su 105 è
                    rumore che fa sembrare gratis chi gratis non è. Sul telefono
                    resta fuori: è quasi sempre vuota e sta nella scheda. */}
                {showMargin && <td data-m="off" className={`num ${Number(s.hourlyRate ?? 0) > 0 ? 'tbl-metric accent' : 'mono mute'}`}>{Number(s.hourlyRate ?? 0) > 0 ? `€ ${fmtEur(s.hourlyRate ?? 0)}/h` : '—'}</td>}
                <td data-m="key" data-l="Stato"><Chip tone={s.active ? 'ok' : 'ghost'} dot>{s.active ? 'Attivo' : 'Inattivo'}</Chip></td>
              </tr>
            ))}
            {!data?.items.length && (
              <tr>
                <td colSpan={showMargin ? 7 : 6} style={{ padding: 0 }}>
                  <EmptyState
                    brand
                    title={search ? 'Nessun risultato' : 'Nessun professionista'}
                    description={search ? 'Prova a modificare la ricerca.' : 'Aggiungi i collaboratori esterni e le agenzie partner per assegnarli ai progetti.'}
                    action={!search ? (
                      <button className="btn btn-primary d-only" onClick={() => openModal('newVendor')}>
                        <Icon name="plus" size={12} /> Aggiungi il primo
                      </button>
                    ) : undefined}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
      )}

      {/* Telefono: la stessa azione che in cima è d-only, qui dove arriva il
          pollice. Il fondo pagina se lo allunga da solo .page:has(.m-actionbar). */}
      <DesktopOnly
        cosa="La creazione di un nuovo professionista"
        perche="richiede anagrafica fiscale, competenze e tariffe; dal telefono puoi consultare, contattare e allegare documenti."
      >
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={() => openModal('newVendor')}>
            <Icon name="plus" /> Nuovo professionista
          </button>
        </div>
      </DesktopOnly>
    </div>
  );
}
